import { Injectable, Injector } from '@angular/core';
import { lastValueFrom, Observable, of } from 'rxjs';
import { HttpDataService } from './http-data.service';
import { map } from 'rxjs/operators';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { CONTENTFUL } from 'core/services/assets/constants';
import { UtilsService } from 'core/services/utils.service';

@Injectable()
export class ConfigurationsService extends HttpDataService {
  constructor(protected injector: Injector) {
    super(injector, '/configurations');
  }

  public getFile(fileName: string): Observable<any> {
    return this.http.get(this.baseUrl, this.getRequestOptions({ fileName }));
  }

  public getConfigurationProfile({ sid }: { sid: string }): Observable<any> {
    return this.http.get(`${this.baseUrl}/profile`, this.getRequestOptions({ sid }));
  }

  /**
   * Get the list of files for resources
   * @param options
   */
  public fetchResources(options?: any): Promise<any> {
    const { page = 1, limit = 12, locale = 'en-US', search } = options;
    const filters: Map<string, any[]> = new Map<string, any[]>(search);
    filters.set('page', page);
    filters.set('limit', limit);
    filters.set('locale', locale);

    return lastValueFrom(this.http.get(`${this.baseUrl}/resources`, this.getRequestOptionsMap(filters))).then(
      (response) => {
        return {
          docs: response['docs'],
          limit: response['limit'],
          page: page,
          pages: Math.ceil(response['total'] / response['limit']),
          total: response['total']
        };
      }
    );
  }

  /**
   * Get the messages of group from configurations (contentful)
   */
  public getTooltipMessage$(keyName: string): Observable<any> {
    return this.getInfoMessage$(
      'toolTip',
      'fields.keys[match]',
      keyName,
      'textoDeAyuda',
      UtilsService.getBrowserLanguageLocale(),
      true
    );
  }

  /**
   * Get the filters for resources
   * @param options
   */
  public getResourceFilters$(options: { lang?: string }): Observable<any> {
    return this.fetchRequest(`${this.baseUrl}/resources/filters`, options);
  }

  /**
   * Get the messages/tooltips/warnings from contentful/configurations
   * @param contentType
   * @param nameKey
   * @param key
   * @param nameField
   * @param locale
   * @param isSingleResponse
   * @param isHtml
   * @param noParagraphs
   * @param isWarningMessage
   * @param descriptionField
   */
  public getInfoMessage$(
    contentType: string,
    nameKey: string,
    key: string,
    nameField: string,
    locale: string,
    isSingleResponse: boolean = false,
    isHtml: boolean = false,
    noParagraphs: boolean = false,
    isWarningMessage: boolean = false,
    descriptionField?: string
  ): Observable<any> {
    if (CONTENTFUL.active) {
      return this.http.get(`${this.baseUrl}/info`, this.getRequestOptions({ contentType, nameKey, key, locale })).pipe(
        map((response: any) => {
          if (response && response.items.length) {
            let message = null;
            if (isHtml) {
              message = documentToHtmlString(response.items[0].fields.text);
              if (noParagraphs) message = message.replace('<p>', '').replace('</p>', '');
            } else if (isSingleResponse) {
              message = response.items[0].fields[nameField];
            } else if (isWarningMessage) {
              message = {
                title: response.items[0].fields[nameField],
                description: documentToHtmlString(response.items[0].fields[descriptionField])
              };
            } else {
              message = {};
              message = response.items.reduce((message, item) => {
                const entryKey = item.fields.keys
                  .find((tempKey) => tempKey.indexOf(key) === 0)
                  .substring(key.length + 1);
                message[entryKey] = item.fields[nameField];
                return message;
              }, message);
            }
            return message;
          } else {
            console.warn('Empty key: ' + key);
          }
        })
      );
    }
    return of(null);
  }
}
